import React from "react";
import {
    CartInTimeline,
    CartKind,
    durationMinutes,
    formatTime,
    isActiveKind
} from "@widgets/reports/cartTimeline/CartTimelineUtils";
import {useTooltip} from "@widgets/tooltip/TooltipProvider";
import { useIntl } from "react-intl";
import {twJoin} from "tailwind-merge";

/** Colors of a bar per kind; they also differ in lightness, not only in hue. */
export const barClassNames: Record<CartKind, string> = {
    active: "bg-blue-600 text-white ring-2 ring-blue-600/30 animate-pulse",
    paying: "bg-amber-400 text-amber-950",
    ordered: "bg-green-700 dark:bg-green-600 text-white",
    pos: "bg-violet-600",
    expired: "bg-gray-200 text-gray-600 border border-gray-300 dark:bg-slate-700 dark:text-slate-300 dark:border-slate-600",
    reset: "bg-white text-red-600 border border-dashed border-red-400 bg-red-100 dark:bg-red-900 dark:text-red-300 dark:border-red-500",
};

type Props = {
    item: CartInTimeline,
    /** Position and width on the axis, in percentages. */
    left: number,
    width: number,
    onSelect: (item: CartInTimeline) => void,
};

/**
 * One cart on the timeline: a bar from start to end, or a dot for a POS
 * order. Click to see its details.
 */
const CartTimelineBar = ({item, left, width, onSelect}: Props) => {
    const intl = useIntl();
    const {applyTooltip} = useTooltip();
    const {cart, kind, start, end} = item;
    const label: string = intl.formatMessage(
        {id: "cartTimeline.bar.label"},
        {
            id: cart.id,
            kind: intl.formatMessage({id: `cartTimeline.kind.${kind}`}),
            start: formatTime(start),
            end: formatTime(end),
            minutes: durationMinutes(start, end),
            fulfillment: intl.formatMessage({id: cart.isDelivery ? "delivery" : "takeaway"}),
        }
    );
    const tooltip: string = intl.formatMessage(
        {
            id: kind === "pos"
                ? "cartTimeline.bar.tooltip.pos"
                : isActiveKind(kind)
                    ? "cartTimeline.bar.tooltip.running"
                    : "cartTimeline.bar.tooltip",
        },
        {
            kind: intl.formatMessage({id: `cartTimeline.kind.${kind}`}),
            start: formatTime(start),
            end: formatTime(end),
        }
    );

    if (kind === "pos") {
        return (
            <button
                type={"button"}
                aria-label={label}
                {...applyTooltip(tooltip)}
                onClick={() => onSelect(item)}
                className={twJoin(
                    "absolute top-1/2 -translate-y-1/2 -translate-x-1/2 size-3 rounded-full cursor-pointer",
                    "outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500",
                    barClassNames.pos,
                )}
                style={{left: `${left}%`}}
            />
        );
    }

    return (
        <button
            type={"button"}
            aria-label={label}
            {...applyTooltip(tooltip)}
            onClick={() => onSelect(item)}
            className={twJoin(
                "absolute top-[3px] h-5 min-w-2 px-1.5 rounded box-border overflow-hidden whitespace-nowrap text-left",
                "text-[11px] leading-5 font-semibold cursor-pointer transition hover:-translate-y-px hover:shadow",
                "outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500",
                barClassNames[kind],
            )}
            style={{left: `${left}%`, width: `${width}%`}}
        >
            {/* Only an ID when it fits, else the bar is too narrow to read it */}
            {width >= 3 ? `#${cart.id}` : ""}
        </button>
    );
};

export default CartTimelineBar;